/**
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { LOCALE_ID, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { AppRoutingModule } from "./app-routing.module";
import { CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AppComponent } from "./app.component";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { MatStepperModule } from "@angular/material/stepper";
import { QRCodeModule } from "angularx-qrcode";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatTabsModule } from "@angular/material/tabs";
import { CookieService } from "ngx-cookie-service";

import { ConfigActions } from "./ThemeOptions/store/config.actions";
import { AvatarModule } from "ngx-avatars";
import { MatDialogModule } from "@angular/material/dialog";
import { CKEditorModule } from "ckeditor4-angular";
import { NgChartsModule } from "ng2-charts";
import { RequestInterceptor } from "./core/interceptors/request.interceptor";
import { ToastrModule } from "ngx-toastr";
import { registerLocaleData } from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireMessagingModule } from "@angular/fire/compat/messaging";
import { environment } from "src/environments/environment";
import { IMqttServiceOptions, MqttModule } from "ngx-mqtt";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";

registerLocaleData(localeFr, "fr");

const MQTT_SERVICE_OPTIONS: IMqttServiceOptions | any = environment.mqtt;

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatProgressSpinnerModule,
    HttpClientModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatInputModule,
    NgbModule,
    FormsModule,
    MatStepperModule,
    DragDropModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatTabsModule,
    CKEditorModule,
    NgChartsModule,
    ReactiveFormsModule,
    ToastrModule.forRoot(),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireMessagingModule,
    MqttModule.forRoot(MQTT_SERVICE_OPTIONS),
    InfiniteScrollDirective,
    QRCodeModule,
    AvatarModule,
    CommonModule,
  ],
  providers: [
    DecimalPipe,
    DatePipe,
    CookieService,
    { provide: LOCALE_ID, useValue: "fr-FR" },
    ConfigActions,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
